import React from 'react';
import { Box, Typography } from '@mui/material';

interface IProps {
  data?: Record<string, unknown> | unknown[] | null;
  depth?: number;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const renderPrimitive = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Sì' : 'No';
  return String(value);
};

const ExternalLeadDataTree = ({ data, depth = 0 }: IProps) => {
  if (data === null || data === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nessun dato.
      </Typography>
    );
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          [vuoto]
        </Typography>
      );
    }
    return (
      <Box sx={{ pl: depth > 0 ? 2 : 0 }}>
        {data.map((item, idx) => (
          <Box key={idx} sx={{ mb: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 'bold' }}
            >
              [{idx}]
            </Typography>
            {isObject(item) || Array.isArray(item) ? (
              <ExternalLeadDataTree
                data={item as Record<string, unknown>}
                depth={depth + 1}
              />
            ) : (
              <Typography variant="body2">{renderPrimitive(item)}</Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  const entries = Object.entries(data);
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nessun dato.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
        rowGap: 0.5,
        columnGap: 2,
        pl: depth > 0 ? 2 : 0,
        borderLeft: depth > 0 ? (th) => `2px solid ${th.palette.divider}` : 0,
      }}
    >
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <Typography variant="body2" color="text.secondary">
            {key}
          </Typography>
          {isObject(value) || Array.isArray(value) ? (
            <ExternalLeadDataTree
              data={value as Record<string, unknown>}
              depth={depth + 1}
            />
          ) : (
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {renderPrimitive(value)}
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ExternalLeadDataTree;
